import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Play, Pause, RotateCcw, X, Check, Trophy, Volume2, VolumeX, UserPlus, Timer, ArrowLeft, TimerReset, Zap } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

/* ---------- Audio (Web Audio API, sin assets) ---------- */
function useBeeper() {
  const ctxRef = useRef(null);
  const enabledRef = useRef(true);

  const ensureCtx = () => {
    if (!enabledRef.current) return null;
    if (!ctxRef.current) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
      } catch { return null; }
    }
    if (ctxRef.current.state === 'suspended') {
      try { ctxRef.current.resume(); } catch { /* ignore */ }
    }
    return ctxRef.current;
  };

  const beep = useCallback((freq = 880, durMs = 150, vol = 0.4) => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durMs / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durMs / 1000 + 0.05);
  }, []);

  return {
    beep,
    setEnabled: (v) => { enabledRef.current = v; },
    ensureCtx,
  };
}

/* ---------- Formatter ---------- */
const fmt = (ms) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/* =========================================================
   MODO 1: CRONÓMETRO con ranking (sube al Hall of Fame)
   ========================================================= */
function ModeRanking({ beep }) {
  const [entrenos, setEntrenos] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEntrenoId, setSelectedEntrenoId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const baseRef = useRef(0);
  const startedAtRef = useRef(0);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [eRes, uRes] = await Promise.all([
          axios.get(`${API}/hall-of-fame/entrenos`),
          axios.get(`${API}/users`),
        ]);
        setEntrenos(eRes.data || []);
        const lista = (uRes.data || []).filter(u => u.aprobado && u.role !== 'entrenador');
        lista.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        setUsers(lista);
      } catch (e) {
        toast.error('Error cargando datos');
      }
    })();
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    let raf;
    const tick = () => {
      setElapsed(baseRef.current + (performance.now() - startedAtRef.current));
      raf = requestAnimationFrame(tick);
    };
    startedAtRef.current = performance.now();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  const selectedEntreno = useMemo(
    () => entrenos.find(e => e.id === selectedEntrenoId),
    [entrenos, selectedEntrenoId]
  );

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    const selectedIds = new Set(clientes.map(c => c.id));
    return users
      .filter(u => !selectedIds.has(u.id) && (u.nombre || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [users, search, clientes]);

  const algunoConTiempo = clientes.some(c => c.tiempo_ms != null || c.reps != null);

  const addCliente = (u) => {
    setClientes(prev => [...prev, { id: u.id, nombre: u.nombre, foto_perfil: u.foto_perfil || null }]);
    setSearch('');
    setSearchOpen(false);
  };
  const removeCliente = (id) => setClientes(prev => prev.filter(c => c.id !== id));

  const start = () => {
    if (!selectedEntrenoId) { toast.error('Elige un entreno primero'); return; }
    if (clientes.length === 0) { toast.error('Añade al menos un cliente'); return; }
    if (running) return;
    baseRef.current = elapsed;
    setRunning(true);
    beep(880, 200);
  };
  const pause = () => { setRunning(false); beep(440, 120); };
  const reset = () => {
    if (running) return;
    if (!window.confirm('¿Resetear cronómetro a 0? Se borrarán los tiempos no guardados.')) return;
    setElapsed(0);
    setClientes(prev => prev.map(c => ({ id: c.id, nombre: c.nombre, foto_perfil: c.foto_perfil })));
    baseRef.current = 0;
  };

  const registrarTiempo = (id) => {
    const entreno = entrenos.find(e => e.id === selectedEntrenoId);
    const isReps = entreno?.tipo === 'reps';
    if (isReps) {
      const cliente = clientes.find(c => c.id === id);
      const previo = cliente?.reps != null ? String(cliente.reps) : '';
      const input = window.prompt(`Repeticiones de ${cliente?.nombre || ''} en ${entreno?.nombre || ''}:`, previo);
      if (input === null) return;
      const n = parseInt(input.trim(), 10);
      if (!Number.isFinite(n) || n < 0) {
        toast.error('Número de repeticiones inválido');
        return;
      }
      beep(1000, 150);
      setClientes(prev => prev.map(c => c.id === id ? { ...c, reps: n } : c));
      return;
    }
    setClientes(prev => prev.map(c => {
      if (c.id !== id) return c;
      if (c.tiempo_ms != null) {
        if (!window.confirm(`${c.nombre} ya tiene ${fmt(c.tiempo_ms)}. ¿Sobrescribir?`)) return c;
      }
      beep(1000, 150);
      return { ...c, tiempo_ms: elapsed };
    }));
  };

  const finalizar = async () => {
    const entreno = entrenos.find(e => e.id === selectedEntrenoId);
    const isReps = entreno?.tipo === 'reps';
    const conTiempo = isReps
      ? clientes.filter(c => c.reps != null)
      : clientes.filter(c => c.tiempo_ms != null);
    if (conTiempo.length === 0) { toast.error(isReps ? 'Ningún cliente tiene reps registradas' : 'Ningún cliente tiene tiempo registrado'); return; }
    if (!window.confirm(`Subir ${conTiempo.length} ${isReps ? 'marca(s)' : 'tiempo(s)'} al Hall of Fame y reiniciar?`)) return;
    setRunning(false);
    setFinishing(true);
    try {
      const fecha = new Date().toISOString().slice(0, 10);
      const results = await Promise.allSettled(
        conTiempo.map(c =>
          axios.post(`${API}/hall-of-fame`, {
            user_id: c.id, entreno_id: selectedEntrenoId,
            tiempo: isReps ? String(c.reps) : fmt(c.tiempo_ms), fecha, notas: 'Cronómetro directo',
          }).then(r => ({ cliente: c, data: r.data }))
        )
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const ko = results.length - ok;
      const records = results
        .filter(r => r.status === 'fulfilled' && r.value?.data?.es_record_personal)
        .map(r => r.value);
      if (ok > 0) toast.success(`🏆 ${ok} marca${ok > 1 ? 's' : ''} subida${ok > 1 ? 's' : ''} al Hall of Fame`);
      records.forEach((r, idx) => {
        const nombre = r.cliente.nombre;
        const valor = isReps ? `${r.cliente.reps} reps` : fmt(r.cliente.tiempo_ms);
        setTimeout(() => {
          toast.success(`⚡ ¡RÉCORD PERSONAL de ${nombre}! ${valor}`, { duration: 6000 });
        }, 500 + idx * 400);
      });
      if (ko > 0) toast.error(`${ko} fallaron al subir`);
      setClientes([]); setElapsed(0); baseRef.current = 0; setSelectedEntrenoId('');
    } catch (e) {
      toast.error('Error al subir al Hall of Fame');
    } finally { setFinishing(false); }
  };

  return (
    <div className="space-y-5" data-testid="mode-ranking">
      {/* Setup */}
      <Card className="bg-gray-950/60 border-gray-800 p-4 sm:p-6">
        <label className="block mb-4">
          <div className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Entreno</div>
          <select
            value={selectedEntrenoId}
            onChange={(e) => setSelectedEntrenoId(e.target.value)}
            disabled={running}
            className="w-full bg-gray-900 border border-gold/40 rounded-xl px-4 py-3 text-white text-lg focus:outline-none focus:border-gold disabled:opacity-50"
            data-testid="select-entreno"
          >
            <option value="">— Elige un entreno del Hall of Fame —</option>
            {entrenos.map(e => <option key={e.id} value={e.id}>{e.nombre}{e.tipo ? ` · ${e.tipo === 'reps' ? 'REPS' : 'TIEMPO'}` : ''}</option>)}
          </select>
          {selectedEntreno?.descripcion && (
            <div className="text-xs text-gray-500 mt-1.5 italic">{selectedEntreno.descripcion}</div>
          )}
        </label>
        <div className="relative">
          <div className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Añadir cliente</div>
          <div className="relative">
            <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              placeholder="Empieza a escribir el nombre…"
              className="w-full pl-9 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white text-base focus:outline-none focus:border-gold/60"
              data-testid="input-search-cliente"
            />
          </div>
          {searchOpen && filteredUsers.length > 0 && (
            <div className="absolute z-30 mt-1 w-full bg-gray-950 border border-gray-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
              {filteredUsers.map(u => (
                <button key={u.id} onClick={() => addCliente(u)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-900 text-left border-b border-gray-900 last:border-b-0">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-300">
                    {u.foto_perfil ? <img src={u.foto_perfil.startsWith('http') ? u.foto_perfil : `${BACKEND_URL}${u.foto_perfil}`} alt="" className="w-full h-full object-cover" /> : (u.nombre || 'U')[0].toUpperCase()}
                  </div>
                  <span className="text-white text-sm">{u.nombre}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {clientes.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {clientes.map(c => (
              <div key={c.id} className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-full pl-1 pr-3 py-1">
                <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-300">
                  {c.foto_perfil ? <img src={c.foto_perfil.startsWith('http') ? c.foto_perfil : `${BACKEND_URL}${c.foto_perfil}`} alt="" className="w-full h-full object-cover" /> : (c.nombre || 'U')[0].toUpperCase()}
                </div>
                <span className="text-sm text-white">{c.nombre}</span>
                {!running && <button onClick={() => removeCliente(c.id)} className="text-gray-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Timer */}
      <Card className="bg-gradient-to-b from-gray-950 to-black border-gold/30 p-6">
        <div className="flex flex-col items-center">
          <div className={`text-[20vw] sm:text-[14vw] md:text-[12vw] lg:text-[9rem] font-black tabular-nums leading-none tracking-tighter ${running ? 'text-emerald-400' : 'text-gold'}`} data-testid="timer-display">
            {fmt(elapsed)}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            {!running ? (
              <Button onClick={start} disabled={!selectedEntrenoId || clientes.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xl px-8 py-7 rounded-2xl disabled:opacity-40" data-testid="btn-start">
                <Play className="h-6 w-6 mr-2" /> {elapsed > 0 ? 'Reanudar' : 'Iniciar'}
              </Button>
            ) : (
              <Button onClick={pause} className="bg-amber-600 hover:bg-amber-700 text-white text-xl px-8 py-7 rounded-2xl"><Pause className="h-6 w-6 mr-2" /> Pausar</Button>
            )}
            <Button onClick={reset} disabled={running || elapsed === 0} variant="outline" className="border-gray-600 text-gray-300 text-lg px-6 py-7 rounded-2xl disabled:opacity-40">
              <RotateCcw className="h-5 w-5 mr-2" /> Reset
            </Button>
          </div>
        </div>
      </Card>

      {/* Tarjetas */}
      {clientes.length > 0 && (
        <Card className="bg-gray-950/60 border-gray-800 p-4 sm:p-5">
          <div className="text-xs text-gray-400 uppercase tracking-widest mb-3">
            {running ? '👇 Toca al cliente cuando termine' : 'Clientes'}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {clientes.map(c => {
              const entrenoSel = entrenos.find(e => e.id === selectedEntrenoId);
              const isReps = entrenoSel?.tipo === 'reps';
              const done = isReps ? c.reps != null : c.tiempo_ms != null;
              return (
                <button key={c.id} onClick={() => running ? registrarTiempo(c.id) : null} disabled={!running}
                  className={`relative flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all min-h-[120px] ${done ? 'bg-emerald-950/40 border-emerald-600' : running ? 'bg-gray-900 border-gray-700 hover:border-gold active:scale-95 cursor-pointer' : 'bg-gray-900/50 border-gray-800'}`}>
                  {done && <div className="absolute top-2 right-2 bg-emerald-600 rounded-full p-1"><Check className="h-4 w-4 text-white" /></div>}
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-base font-bold text-gray-300 mb-2">
                    {c.foto_perfil ? <img src={c.foto_perfil.startsWith('http') ? c.foto_perfil : `${BACKEND_URL}${c.foto_perfil}`} alt="" className="w-full h-full object-cover" /> : (c.nombre || 'U')[0].toUpperCase()}
                  </div>
                  <div className={`text-sm font-semibold text-center ${done ? 'text-emerald-300 line-through opacity-70' : 'text-white'}`}>{c.nombre}</div>
                  {done && (
                    <div className="text-lg font-black text-emerald-400 tabular-nums mt-1">
                      {isReps ? `${c.reps} reps` : fmt(c.tiempo_ms)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {algunoConTiempo && (
        <div className="flex justify-center">
          <Button onClick={finalizar} disabled={finishing} className="bg-gold hover:bg-amber-500 text-black text-xl font-black px-8 py-7 rounded-2xl shadow-2xl shadow-gold/20" data-testid="btn-finalizar">
            <Trophy className="h-6 w-6 mr-2" />{finishing ? 'Subiendo…' : 'Finalizar y subir al Hall of Fame'}
          </Button>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   MODO 2: CUENTA ATRÁS
   ========================================================= */
function ModeCountdown({ beep }) {
  const [min, setMin] = useState(5);
  const [sec, setSec] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | pre | run | done
  const [preCount, setPreCount] = useState(0);
  const endRef = useRef(0);
  const beepedRef = useRef(new Set());

  const totalMs = (Number(min) || 0) * 60000 + (Number(sec) || 0) * 1000;

  useEffect(() => {
    if (phase !== 'pre') return undefined;
    const nRef = { current: 5 };
    setPreCount(5);
    beep(440, 200);
    const id = setInterval(() => {
      nRef.current -= 1;
      const n = nRef.current;
      if (n > 0) { setPreCount(n); beep(660, 200); }
      else {
        setPreCount(0); beep(1200, 500, 0.5); clearInterval(id);
        endRef.current = performance.now() + totalMs;
        beepedRef.current = new Set();
        setPhase('run'); setRunning(true);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase, beep, totalMs]);

  useEffect(() => {
    if (phase !== 'run' || !running) return undefined;
    let raf;
    const tick = () => {
      const rem = Math.max(0, endRef.current - performance.now());
      setRemaining(rem);
      const secLeft = Math.ceil(rem / 1000);
      if (rem > 0 && secLeft <= 3 && !beepedRef.current.has(secLeft)) {
        beepedRef.current.add(secLeft); beep(880, 180);
      }
      if (rem <= 0) {
        setRunning(false); setPhase('done'); beep(1200, 700, 0.6); return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, running, beep]);

  const start = () => {
    if (totalMs <= 0) return;
    setRemaining(totalMs); setPhase('pre');
  };
  const reset = () => {
    setRunning(false); setPhase('idle'); setRemaining(totalMs); setPreCount(0);
    beepedRef.current = new Set();
  };

  const f = fmt(remaining);
  const isDone = phase === 'done';

  return (
    <div className="flex flex-col items-center w-full py-6" data-testid="mode-countdown">
      {phase === 'pre' ? (
        <div className="text-[30vw] sm:text-[22vw] md:text-[18vw] font-black text-amber-500 leading-none tabular-nums">{preCount}</div>
      ) : (
        <div className={`text-[20vw] sm:text-[14vw] md:text-[12vw] lg:text-[9rem] font-black tabular-nums leading-none tracking-tighter ${isDone ? 'text-red-500 animate-pulse' : 'text-gold'}`}>{f}</div>
      )}
      {phase === 'idle' && (
        <div className="flex items-center gap-3 mt-6 text-gray-300">
          <input type="number" min="0" value={min} onChange={(e) => setMin(e.target.value)} className="w-24 bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-white text-2xl text-center" data-testid="countdown-min" />
          <span className="text-xl">min</span>
          <input type="number" min="0" max="59" value={sec} onChange={(e) => setSec(e.target.value)} className="w-24 bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-white text-2xl text-center" data-testid="countdown-sec" />
          <span className="text-xl">seg</span>
        </div>
      )}
      <div className="flex flex-wrap gap-3 mt-8">
        {phase === 'idle' && (
          <Button onClick={start} disabled={totalMs === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white text-2xl px-10 py-8 rounded-2xl" data-testid="btn-cd-start">
            <Play className="h-8 w-8 mr-2" /> Iniciar (5s preaviso)
          </Button>
        )}
        {(phase === 'run' || phase === 'pre') && (
          <Button onClick={reset} className="bg-amber-600 hover:bg-amber-700 text-white text-2xl px-10 py-8 rounded-2xl"><Pause className="h-8 w-8 mr-2" /> Cancelar</Button>
        )}
        <Button onClick={reset} variant="outline" className="border-gray-600 text-gray-300 text-xl px-8 py-8 rounded-2xl"><RotateCcw className="h-6 w-6 mr-2" /> Reset</Button>
      </div>
      {isDone && <div className="text-3xl text-red-500 font-bold mt-6 animate-pulse">¡TIEMPO!</div>}
    </div>
  );
}

/* =========================================================
   MODO 3: ENTRENO POR TIEMPO (estilo Tabata, trabajo/descanso/rondas)
   ========================================================= */
function ModeEntrenoPorTiempo({ beep }) {
  const [workSec, setWorkSec] = useState(20);
  const [restSec, setRestSec] = useState(10);
  const [rounds, setRounds] = useState(8);
  const [phase, setPhase] = useState('idle'); // idle | pre | work | rest | done
  const [currentRound, setCurrentRound] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [preCount, setPreCount] = useState(0);
  const phaseEndRef = useRef(0);
  const beepedRef = useRef(new Set());

  const startPhase = useCallback((p) => {
    const dur = p === 'work' ? Number(workSec) : Number(restSec);
    phaseEndRef.current = performance.now() + dur * 1000;
    beepedRef.current = new Set();
    setRemaining(dur * 1000);
    setPhase(p);
  }, [workSec, restSec]);

  useEffect(() => {
    if (phase !== 'pre') return undefined;
    const nRef = { current: 5 };
    setPreCount(5); beep(440, 200);
    const id = setInterval(() => {
      nRef.current -= 1;
      const n = nRef.current;
      if (n > 0) { setPreCount(n); beep(660, 200); }
      else {
        clearInterval(id); beep(1200, 500, 0.5);
        setCurrentRound(1);
        startPhase('work');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase, beep, startPhase]);

  useEffect(() => {
    if (phase !== 'work' && phase !== 'rest') return undefined;
    let raf;
    const tick = () => {
      const rem = Math.max(0, phaseEndRef.current - performance.now());
      setRemaining(rem);
      const secLeft = Math.ceil(rem / 1000);
      if (rem > 0 && secLeft <= 3 && !beepedRef.current.has(secLeft)) {
        beepedRef.current.add(secLeft); beep(880, 180);
      }
      if (rem <= 0) {
        if (phase === 'work') {
          if (currentRound >= Number(rounds)) {
            beep(1200, 800, 0.6); setPhase('done'); return;
          }
          beep(440, 400); startPhase('rest');
        } else {
          beep(1000, 400); setCurrentRound(r => r + 1); startPhase('work');
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, currentRound, beep, rounds, startPhase]);

  const start = () => {
    if (Number(workSec) <= 0 || Number(rounds) <= 0) return;
    setCurrentRound(0); setPhase('pre');
  };
  const stop = () => { setPhase('idle'); setRemaining(0); setCurrentRound(0); };

  const f = fmt(remaining);
  const isWork = phase === 'work';
  const isRest = phase === 'rest';
  const bg = isWork ? 'bg-emerald-950/40' : isRest ? 'bg-blue-950/40' : '';
  const fg = isWork ? 'text-emerald-400' : isRest ? 'text-blue-300' : 'text-gold';

  return (
    <div className={`flex flex-col items-center w-full rounded-2xl transition-colors p-4 ${bg}`} data-testid="mode-entreno-tiempo">
      {phase === 'pre' ? (
        <div className="text-[30vw] sm:text-[22vw] md:text-[18vw] font-black text-amber-500 leading-none tabular-nums">{preCount}</div>
      ) : phase === 'idle' ? (
        <div className="grid grid-cols-3 gap-4 mt-4 text-gray-300 w-full max-w-2xl">
          <label className="flex flex-col items-center gap-2">
            <span className="text-sm text-emerald-400 font-bold">TRABAJO (seg)</span>
            <input type="number" min="1" value={workSec} onChange={(e) => setWorkSec(e.target.value)} className="w-full bg-gray-900 border border-emerald-700 rounded-xl px-3 py-4 text-white text-3xl text-center font-bold" data-testid="tabata-work" />
          </label>
          <label className="flex flex-col items-center gap-2">
            <span className="text-sm text-blue-300 font-bold">DESCANSO (seg)</span>
            <input type="number" min="0" value={restSec} onChange={(e) => setRestSec(e.target.value)} className="w-full bg-gray-900 border border-blue-700 rounded-xl px-3 py-4 text-white text-3xl text-center font-bold" data-testid="tabata-rest" />
          </label>
          <label className="flex flex-col items-center gap-2">
            <span className="text-sm text-gold font-bold">RONDAS</span>
            <input type="number" min="1" value={rounds} onChange={(e) => setRounds(e.target.value)} className="w-full bg-gray-900 border border-gold/40 rounded-xl px-3 py-4 text-white text-3xl text-center font-bold" data-testid="tabata-rounds" />
          </label>
        </div>
      ) : (
        <>
          <div className={`text-base sm:text-xl uppercase tracking-widest font-bold mb-2 ${fg}`}>
            {isWork ? '🔥 TRABAJO' : isRest ? '💧 DESCANSO' : phase === 'done' ? '✅ COMPLETADO' : ''}
          </div>
          <div className={`text-[20vw] sm:text-[14vw] md:text-[12vw] lg:text-[9rem] font-black tabular-nums leading-none tracking-tighter ${fg}`}>{f}</div>
          <div className="text-2xl text-gray-300 mt-2">
            Ronda <b className="text-white">{Math.min(currentRound, Number(rounds))}</b> / {rounds}
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-3 mt-8">
        {phase === 'idle' && (
          <Button onClick={start} className="bg-emerald-600 hover:bg-emerald-700 text-white text-2xl px-10 py-8 rounded-2xl" data-testid="btn-tiempo-start">
            <Play className="h-8 w-8 mr-2" /> Iniciar (5s preaviso)
          </Button>
        )}
        {phase !== 'idle' && (
          <Button onClick={stop} className="bg-red-600 hover:bg-red-700 text-white text-2xl px-10 py-8 rounded-2xl"><Pause className="h-8 w-8 mr-2" /> Parar</Button>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   PAGE con tabs
   ========================================================= */
export default function EntrenoTimer() {
  const navigate = useNavigate();
  const { beep, setEnabled, ensureCtx } = useBeeper();
  const [soundOn, setSoundOn] = useState(true);
  const [mode, setMode] = useState('ranking');

  useEffect(() => { setEnabled(soundOn); }, [soundOn, setEnabled]);

  useEffect(() => {
    const lockRef = { current: null };
    const request = async () => {
      try {
        if ('wakeLock' in navigator) {
          lockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    request();
    const onVis = () => { if (document.visibilityState === 'visible') request(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (lockRef.current) try { lockRef.current.release(); } catch { /* ignore */ }
    };
  }, []);

  const handleToggleSound = () => {
    setSoundOn(s => {
      const next = !s;
      if (next) { ensureCtx(); beep(660, 80); }
      return next;
    });
  };

  const tabs = [
    { key: 'ranking', label: 'Cronómetro', icon: Timer },
    { key: 'countdown', label: 'Cuenta atrás', icon: TimerReset },
    { key: 'tiempo', label: 'Entreno por tiempo', icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="entreno-timer-page">
      <Navbar />
      <div className="max-w-5xl mx-auto p-3 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-300 hover:text-gold flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-900 transition-colors" data-testid="btn-back">
              <ArrowLeft className="h-5 w-5" /><span className="hidden sm:inline">Volver</span>
            </button>
            <h1 className="text-2xl sm:text-3xl font-black text-gold flex items-center gap-2">
              <Timer className="h-7 w-7" /> Cronómetro Entreno
            </h1>
          </div>
          <button onClick={handleToggleSound} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-xs ${soundOn ? 'bg-emerald-900/40 border border-emerald-700 text-emerald-300' : 'bg-gray-900 border border-gray-700 text-gray-400'}`} data-testid="btn-toggle-sound">
            {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {soundOn ? 'Sonido ON' : 'Sonido OFF'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-gray-950 border border-gray-800 rounded-2xl p-2" data-testid="timer-tabs">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = mode === t.key;
            return (
              <button key={t.key} onClick={() => setMode(t.key)} className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm sm:text-base font-bold ${active ? 'bg-gold text-black' : 'text-gray-300 hover:bg-gray-900'}`} data-testid={`tab-${t.key}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        {mode === 'ranking' && <ModeRanking beep={beep} />}
        {mode === 'countdown' && <ModeCountdown beep={beep} />}
        {mode === 'tiempo' && <ModeEntrenoPorTiempo beep={beep} />}

        <p className="text-xs text-gray-500 text-center mt-6">
          Tip tablet: activa el sonido tocando el botón una vez · la pantalla se mantiene encendida en esta página.
        </p>
      </div>
    </div>
  );
}
